import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { openOutline, closeOutline, informationCircleOutline } from 'ionicons/icons';
import ProPageModal from './ProPageModal';

export interface ProExternalLinkSheetProps {
  /** Controls visibility. */
  isOpen: boolean;
  /** Called when the sheet is dismissed via any path (including Cancel). */
  onDismiss: () => void;
  /** Human-readable label of what will open (e.g. "House Schedule"). */
  title: string;
  /** The URL that will be opened on confirm. Shown to the user for verification. */
  url: string;
  /**
   * Optional extended description shown under the title. Falls back to a
   * standard "outside the app" disclosure if not provided.
   */
  description?: string;
}

/**
 * Pro mobile external-link confirmation modal.
 *
 * Shows the user what's about to open, the destination URL, and a clear
 * disclosure that the link goes to the system browser outside the Pro app.
 * Tapping Open routes through `window.open` (Capacitor WKWebView routes
 * this to the system browser on iOS/Android). Cancel dismisses without
 * navigating.
 *
 * Used by dashboard widgets that link out to external resources
 * (e.g. House/Senate schedule PDFs on `ProCalendarCard`).
 */
const ProExternalLinkSheet: React.FC<ProExternalLinkSheetProps> = ({
  isOpen,
  onDismiss,
  title,
  url,
  description,
}) => {
  const { t } = useTranslation('pro');
  const [showDisclosure, setShowDisclosure] = useState(false);

  // Collapse the disclosure every time the sheet is reopened so it defaults
  // to hidden for each new link.
  useEffect(() => {
    if (!isOpen) setShowDisclosure(false);
  }, [isOpen]);

  const handleOpen = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    onDismiss();
  };

  return (
    <ProPageModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={t('bottomSheet.externalLinkTitle')}
    >
      <div className="px-4 py-4 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary dark:text-text-dark-tertiary mb-1">
            {t('bottomSheet.externalLinkOpening')}
          </p>
          <div className="flex items-start gap-2">
            <p className="flex-1 text-base font-semibold text-text-primary dark:text-text-dark-primary">
              {title}
            </p>
            <button
              type="button"
              onClick={() => setShowDisclosure((v) => !v)}
              aria-label={t('bottomSheet.externalLinkInfoAria')}
              aria-expanded={showDisclosure}
              aria-controls="pro-external-link-disclosure"
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-800/50 transition-colors active:bg-amber-100 dark:active:bg-amber-900/40"
            >
              <IonIcon icon={informationCircleOutline} className="text-lg" />
            </button>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary dark:text-text-dark-tertiary mb-1">
            {t('bottomSheet.externalLinkUrl')}
          </p>
          <p className="text-xs font-mono text-text-secondary dark:text-text-dark-secondary break-all">
            {url}
          </p>
        </div>

        {showDisclosure && (
          <div
            id="pro-external-link-disclosure"
            role="region"
            className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 px-3 py-2.5"
          >
            <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
              {description || t('bottomSheet.externalLinkDisclosure')}
            </p>
          </div>
        )}

        <div className="space-y-2 pt-1">
          <button
            type="button"
            onClick={handleOpen}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-700 text-white font-semibold text-sm transition-colors active:bg-primary-800"
          >
            <IonIcon icon={openOutline} className="text-lg" />
            {t('bottomSheet.externalLinkConfirm')}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-transparent text-text-secondary dark:text-text-dark-secondary font-semibold text-sm transition-colors active:bg-black/5 dark:active:bg-white/5"
          >
            <IonIcon icon={closeOutline} className="text-lg" />
            {t('bottomSheet.externalLinkCancel')}
          </button>
        </div>
      </div>
    </ProPageModal>
  );
};

export default ProExternalLinkSheet;
